import React, { PropsWithChildren, ReactElement, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { ModalFadeIn, ScaleIn } from './Animations'
import { IconButton } from './Button'
import Close from '../icons/Close'
import NavbarController from '../state/NavbarController'

const ModalContainer = styled.div`
  background-color: rgba(17, 17, 17, 0.14);
  backdrop-filter: blur(2px);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 101;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${ModalFadeIn} 0.2s;
  padding: 16px;
`

const ModalInner = styled.div<{ minHeight?: string }>`
  background-color: white;
  border: 1px solid rgba(226, 232, 240, 0.95);
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.12);
  min-width: min(450px, calc(100vw - 32px));
  ${({ minHeight }) => minHeight && `min-height: ${minHeight};`}
  max-width: calc(100vw - 32px);
  border-radius: 22px;
  animation: ${ScaleIn} 0.18s ease-out;
  max-height: calc(100vh - 32px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  overscroll-behavior: contain;

  > * {
    box-sizing: border-box;
    max-width: 100%;
  }

  @media (max-width: 520px) {
    min-width: 0;
    width: 100%;
  }
`

const ModalBody = styled.div<{ $hasHeader: boolean }>`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 12px;
  min-height: 0;
  overflow: auto;
  padding: ${({ $hasHeader }) => $hasHeader ? '16px 20px 18px' : '18px 20px 20px'};
`

export const ModalFooter = styled.div<{ $fullWidthAction?: boolean }>`
  display: flex;
  justify-content: ${({ $fullWidthAction }) => $fullWidthAction ? 'stretch' : 'flex-end'};
  gap: 10px;
  margin-top: auto;
  padding-top: 14px;
  border-top: 1px solid #eef1f5;

  > * {
    ${({ $fullWidthAction }) => $fullWidthAction ? 'flex: 1 1 auto;' : ''}
  }
`

export type ModalProps = PropsWithChildren<{
  visible: boolean
  onClose: any
  innerStyles?: React.CSSProperties
  title?: string
  minHeight?: string
  uncloseable?: boolean
}>

const Modal = (props: ModalProps): ReactElement => {
  const inner = useRef<HTMLDivElement>(null)

  const [isMouseDown, setIsMouseDown] = useState(false)

  useEffect(() => {
    NavbarController.setModalOpen(props.visible)
    return () => NavbarController.setModalOpen(false)
  }, [props.visible])

  if (!props.visible) {
    return <></>
  }

  return <ModalContainer
    onMouseDown={e => {
      if (!(e.target === inner.current || inner.current?.contains(e.target as any))) {
        setIsMouseDown(true)
      }
    }}
    onMouseUp={e => {
      if (!(e.target === inner.current || inner.current?.contains(e.target as any)) && isMouseDown) {
        if (!props.uncloseable) {
          props.onClose()
        }
      }
      setIsMouseDown(false)
    }}>
    <ModalInner
      minHeight={props.minHeight}
      ref={inner}
      style={props.innerStyles}>

      {
        props.title &&
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          padding: '16px 20px 14px',
          alignItems: 'center',
          borderBottom: '1px solid #eef1f5',
          backgroundColor: '#ffffff'
        }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: '#161c27', lineHeight: 1.2 }}>{props.title}</span>
          {
            !props.uncloseable &&
            <IconButton type='button' onClick={() => props.onClose()} style={{ padding: 5, borderRadius: 10 }}>
              <Close style={{ width: '15px' }} />
            </IconButton>
          }
        </div>
      }

      <ModalBody $hasHeader={!!props.title}>
        {props.children}
      </ModalBody>
    </ModalInner>
  </ModalContainer>
}

export default Modal
